import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ActionButton } from './DesignSystem';
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';
import { resolveTradeUnitPrice, resolveCasePrice, getCaseQuantity, type PricingTier } from '../utils/wholesaleProduct';

type Customer = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postcode?: string;
  country?: string;
  companyName?: string;
  companyNumber?: string;
  credit_terms?: number;
  credit_balance?: number;
  customer_type?: string;
  pricing_tier?: PricingTier | string | null;
};
type Product = {
  id: string; name: string; price: number; sku: string;
  wholesale_price?: number; tier_a_price?: number; tier_b_price?: number; tier_c_price?: number;
  case_price?: number; case_quantity?: number; box_quantity?: number;
};

type InvoiceItem = {
  productId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number; // percentage discount per line
  vatRate: number;
  saleType: 'unit' | 'box';
  box_quantity?: number;
};

const InvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  // State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isGuest, setIsGuest] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [creditTermsDays, setCreditTermsDays] = useState<number>(30);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [appliedCredit, setAppliedCredit] = useState<number>(0);
  const [fetchingCustomer, setFetchingCustomer] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contractPrices, setContractPrices] = useState<Record<string, number>>({});

  // Status lifecycle + payment terms + tax mode
  const [invoiceStatus, setInvoiceStatus] = useState<'Draft'|'Sent'|'Paid'|'Overdue'|'Cancelled'>('Draft');
  const [paymentTerms, setPaymentTerms] = useState<string>('Net 30');
  const [taxMode, setTaxMode] = useState<'exclusive'|'inclusive'>('exclusive');

  // This is a create-only form — never lock the form before submission
  const isLocked = false;

  const PAYMENT_TERMS: Record<string, number> = {
    'Due on Receipt': 0,
    'Net 7': 7,
    'Net 14': 14,
    'Net 30': 30,
    'Net 60': 60,
  };

  const STATUS_COLORS: Record<string, string> = {
    Draft: '#6b7280', Sent: '#3b82f6',
    Paid: '#10b981', Overdue: '#ef4444', Cancelled: '#9ca3af',
  };

  const [items, setItems] = useState<InvoiceItem[]>([
    { productId: '', description: '', quantity: 1, unitPrice: 0, discount: 0, vatRate: 20, saleType: 'unit' }
  ]);
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    invoiceNumber: `INV-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${String(Date.now()).slice(-4)}`,
    invoiceDate: today,
    dueDate: (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().split('T')[0]; })(),
    customerAddress: '',
    email: '',
    phone: '',
    companyName: '',
    companyNumber: '',
    description: '',
    status: 'Unpaid',
    notes: ''
  });

  // Load customers and products
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    // Fetch customers
    apiFetch(`${API_URL}/customers`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const customerList = Array.isArray(data) ? data : [];
        setCustomers(customerList.map((c: any) => ({
          id: c._id || c.id,
          name: c.name,
          email: c.email || '',
          phone: c.phone || '',
          address: c.address || '',
          city: c.city || '',
          postcode: c.postcode || '',
          country: c.country || '',
          companyName: c.companyName || c.companyname || '',
          companyNumber: c.companyNumber || c.companynumber || '',
          credit_terms: c.credit_terms || 30,
          credit_balance: c.credit_balance || 0,
          customer_type: c.customer_type || c.customerType || 'Retail',
          pricing_tier: c.pricing_tier || c.pricingTier || null,
        })));
      })
      .catch(err => {
        console.error('Failed to load customers:', err);
        setCustomers([]);
      });

    // Fetch products
    apiFetch(`${API_URL}/products`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const arrRaw = Array.isArray(data) ? data : data.products || [];
        const productList = arrRaw.map((p: any) => ({
          id: p._id || p.id,
          name: p.name,
          price: Number(p.price) || 0,
          sku: p.sku || '',
          wholesale_price: p.wholesale_price != null ? Number(p.wholesale_price) : undefined,
          tier_a_price: p.tier_a_price != null ? Number(p.tier_a_price) : undefined,
          tier_b_price: p.tier_b_price != null ? Number(p.tier_b_price) : undefined,
          tier_c_price: p.tier_c_price != null ? Number(p.tier_c_price) : undefined,
          case_price: p.case_price != null ? Number(p.case_price) : undefined,
          case_quantity: p.case_quantity != null ? Number(p.case_quantity) : undefined,
          box_quantity: p.box_quantity != null ? Number(p.box_quantity) : undefined,
        }));
        setProducts(productList);
      })
      .catch(err => {
        console.error('Failed to load products:', err);
        setProducts([]);
      });
  }, [navigate, token]);

  // Auto-calculate due date from invoice date + credit terms
  const calcDueDate = (invoiceDateStr: string, days: number): string => {
    const d = new Date(invoiceDateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  // Handle customer selection — fetch full data and auto-fill
  // Normalise PostgreSQL-lowercased column names with camelCase fallbacks
  const n = (a: any, b: any) => a || b || '';

  const handleCustomerSelect = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(customer.name);
    setFetchingCustomer(true);
    try {
      apiFetch(`${API_URL}/customers/${customer.id}/prices`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then((prices: any[]) => {
          const map: Record<string, number> = {};
          prices.forEach((p: any) => { map[String(p.product_id)] = parseFloat(p.price); });
          setContractPrices(map);
        })
        .catch(() => setContractPrices({}));

      const res = await apiFetch(`${API_URL}/customers/${customer.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const c = await res.json();
        const terms = c.credit_terms || 30;
        // PostgreSQL may return lowercase: address, city, postcode, country are fine
        // but companyName → companyname, companyNumber → companynumber
        const addressBlock = [c.address, c.city, c.postcode, c.country].filter(Boolean).join(', ');
        const resolvedCompanyName = n(c.companyName, c.companyname);
        const resolvedCompanyNumber = n(c.companyNumber, c.companynumber);
        const resolvedDigitalId = n(c.digitalId, n(c.digital_id, c.digitalid));
        const resolvedSicCode = n(c.sicCode, n(c.sic_code, c.siccode));
        setCreditTermsDays(terms);
        setAppliedCredit(0);

        // Fetch live credit balance from credit notes (not the stale cached column)
        try {
          const creditRes = await apiFetch(`${API_URL}/credit-notes/customer/${c.id || c._id}/balance`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (creditRes.ok) {
            const creditData = await creditRes.json();
            setCreditBalance(parseFloat(creditData.credit_balance) || 0);
          } else {
            setCreditBalance(0);
          }
        } catch {
          setCreditBalance(Number(c.credit_balance) || 0);
        }
        setForm(f => ({
          ...f,
          customerAddress: addressBlock,
          email: c.email || '',
          phone: c.phone || '',
          companyName: resolvedCompanyName,
          companyNumber: resolvedCompanyNumber,
          dueDate: calcDueDate(f.invoiceDate, terms)
        }));
        // Update local customer record with full data
        setSelectedCustomer({
          id: c.id || c._id,
          name: c.name,
          email: c.email || '',
          phone: c.phone || '',
          address: c.address || '',
          city: c.city || '',
          postcode: c.postcode || '',
          country: c.country || '',
          companyName: resolvedCompanyName,
          companyNumber: resolvedCompanyNumber,
          credit_terms: terms,
          credit_balance: 0,
          customer_type: c.customer_type || c.customerType || customer.customer_type || 'Retail',
          pricing_tier: c.pricing_tier || c.pricingTier || customer.pricing_tier || null,
        });
      }
    } catch (err) {
      console.error('Failed to fetch customer details:', err);
      // fallback to what we have
      setForm(f => ({ ...f, customerAddress: customer.address || '' }));
    } finally {
      setFetchingCustomer(false);
    }
  };

  // Handle product selection
  const handleProductSelect = (idx: number, productId: string) => {
    if (!productId) {
      return;
    }

    const product = products.find(p => String(p.id) === String(productId));

    if (product) {
      const isWholesale = selectedCustomer?.customer_type === 'Wholesale';
      const tier = (selectedCustomer?.pricing_tier || null) as PricingTier | null;
      const contractPrice = contractPrices[String(product.id)];
      const unitPrice = resolveTradeUnitPrice(product, { pricingTier: tier, isWholesale, contractPrice });
      const newItems = [...items];
      newItems[idx] = {
        ...newItems[idx],
        productId: String(product.id),
        description: product.name,
        unitPrice,
        saleType: 'unit',
        box_quantity: getCaseQuantity(product) || product.box_quantity,
      };
      setItems(newItems);

      // Auto-add new row if last item
      if (idx === items.length - 1) {
        setItems([...newItems, { productId: '', description: '', quantity: 1, unitPrice: 0, discount: 0, vatRate: 20, saleType: 'unit' }]);
      }
    } else {
      console.error('❌ Product NOT FOUND! Selected:', productId, 'Available:', products.map(p => p.id));
    }
  };

  const handleSaleTypeChange = (idx: number, saleType: 'unit' | 'box') => {
    const product = products.find(p => String(p.id) === String(items[idx].productId));
    if (!product) return;
    const newItems = [...items];
    const isWholesale = selectedCustomer?.customer_type === 'Wholesale';
    const tier = (selectedCustomer?.pricing_tier || null) as PricingTier | null;
    const contractPrice = contractPrices[String(product.id)];
    if (saleType === 'box') {
      const bq = getCaseQuantity(product) || product.box_quantity || 0;
      if (!bq) return;
      const unitPrice = resolveTradeUnitPrice(product, { pricingTier: tier, isWholesale, contractPrice });
      const boxPrice = resolveCasePrice(product, unitPrice, { pricingTier: tier, isWholesale, contractPrice }) ?? unitPrice * bq;
      newItems[idx] = { ...newItems[idx], saleType, unitPrice: boxPrice, box_quantity: bq };
    } else {
      const unitPrice = resolveTradeUnitPrice(product, { pricingTier: tier, isWholesale, contractPrice });
      newItems[idx] = { ...newItems[idx], saleType, unitPrice, box_quantity: getCaseQuantity(product) || product.box_quantity };
    }
    setItems(newItems);
  };

  // Update item field
  const handleItemChange = (idx: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    setItems(newItems);
  };

  // Add item
  const addItem = () => {
    setItems([...items, { productId: '', description: '', quantity: 1, unitPrice: 0, discount: 0, vatRate: 20, saleType: 'unit' }]);
  };

  // Remove item
  const removeItem = (idx: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== idx));
    }
  };

  // Calculate totals — supports tax inclusive or exclusive mode
  const calculateTotals = () => {
    let subtotal = 0;
    let discountTotal = 0;
    let vatAmount = 0;

    items.forEach(item => {
      const gross = (item.quantity || 0) * (item.unitPrice || 0);
      const discountAmt = gross * ((item.discount || 0) / 100);
      const net = gross - discountAmt;
      subtotal += gross;
      discountTotal += discountAmt;
      if (taxMode === 'exclusive') {
        // VAT is added on top of net
        vatAmount += (net * (item.vatRate || 0)) / 100;
      } else {
        // VAT is already included in unit price — extract it
        vatAmount += net - net / (1 + (item.vatRate || 0) / 100);
      }
    });

    const netAfterDiscount = subtotal - discountTotal;
    const total = taxMode === 'exclusive'
      ? netAfterDiscount + vatAmount - appliedCredit
      : netAfterDiscount - appliedCredit; // VAT already included
    return {
      subtotal,
      discountTotal,
      vatAmount,
      total: Math.max(0, total)
    };
  };

  const totals = calculateTotals();

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // prevent double-submit

    const customerName = isGuest ? searchTerm : selectedCustomer?.name || '';

    if (!customerName) {
      alert('❌ Please select or enter customer name');
      return;
    }

    if (!form.dueDate) {
      alert('❌ Please select due date');
      return;
    }

    const payload = {
      customerId: isGuest ? null : (selectedCustomer?.id || null),
      clientName: customerName,
      customerEmail: form.email,
      customerPhone: form.phone,
      companyName: form.companyName,
      companyNumber: form.companyNumber,
      customerAddress: form.customerAddress,
      invoiceNumber: form.invoiceNumber,
      invoiceDate: form.invoiceDate,
      dueDate: form.dueDate,
      paymentDueDate: form.dueDate,
      amount: totals.total,
      status: invoiceStatus,
      description: form.description,
      notes: form.notes,
      appliedCredit,
      items: items
        .filter(item => item.description && item.quantity > 0)
        .map(item => ({
          productId: item.productId || null,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          unit_price: item.unitPrice,
          discount: item.discount || 0,
          vatRate: item.vatRate,
          vat_rate: item.vatRate,
          saleType: item.saleType,
          sale_type: item.saleType,
          box_quantity: item.box_quantity ?? null,
        })),
      subtotal: totals.subtotal,
      discountTotal: totals.discountTotal,
      vatAmount: totals.vatAmount,
      total: totals.total
    };

    try {
      setSubmitting(true);
      const res = await apiFetch(`${API_URL}/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert('✅ Invoice created successfully!');
        navigate('/invoices/list');
      } else {
        const error = await res.json();
        alert(`❌ Failed: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('❌ Network error');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered customers for search
  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
    <style>{`
      @media (max-width: 640px) {
        .invoice-row { grid-template-columns: 1fr !important; }
        .invoice-autofill-grid { grid-template-columns: 1fr !important; }
        .invoice-table-wrapper { font-size: 12px; }
        .invoice-submit-row { flex-direction: column; }
        .invoice-submit-row button { width: 100% !important; }
      }
    `}</style>
    <form onSubmit={handleSubmit} style={styles.form}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <h2 style={styles.title}>🧾 Create Invoice</h2>
          <span style={{
            padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700,
            background: STATUS_COLORS[invoiceStatus] + '22',
            color: STATUS_COLORS[invoiceStatus], border: `1.5px solid ${STATUS_COLORS[invoiceStatus]}`,
          }}>{invoiceStatus}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={styles.invoiceNumber}>{form.invoiceNumber}</div>
          {/* Status lifecycle selector */}
          <select
            value={invoiceStatus}
            onChange={e => setInvoiceStatus(e.target.value as any)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            title="Change invoice status"
          >
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Customer Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Customer Information</h3>

        {/* Guest Toggle */}
        <div style={styles.toggleButtons}>
          <button
            type="button"
            onClick={() => setIsGuest(false)}
            style={{
              ...styles.toggleBtn,
              ...(isGuest ? styles.toggleBtnInactive : styles.toggleBtnActive)
            }}
          >
            📋 Existing Customer
          </button>
          <button
            type="button"
            onClick={() => setIsGuest(true)}
            style={{
              ...styles.toggleBtn,
              ...(!isGuest ? styles.toggleBtnInactive : styles.toggleBtnActive)
            }}
          >
            👤 Guest Customer
          </button>
        </div>

        {/* Customer Search/Input */}
        {!isGuest ? (
          <div style={styles.searchWrapper}>
            <label style={styles.label}>Search Customer *</label>
            <input
              type="text"
              placeholder="Start typing customer name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedCustomer(null);
              }}
              style={styles.input}
            />
            {searchTerm && !selectedCustomer && filteredCustomers.length > 0 && (
              <div style={styles.dropdown}>
                {filteredCustomers.slice(0, 8).map(c => (
                  <div
                    key={c.id}
                    style={styles.dropdownItem}
                    onClick={() => handleCustomerSelect(c)}
                  >
                    <strong>{c.name}</strong>
                    {(c.companyName || (c as any).companyname) && (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{c.companyName || (c as any).companyname}</div>
                    )}
                    {c.email && <div style={styles.dropdownAddress}>{c.email}</div>}
                    {c.address && <div style={styles.dropdownAddress}>{c.address}{c.city ? `, ${c.city}` : ''}</div>}
                  </div>
                ))}
              </div>
            )}
            {fetchingCustomer && (
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}>⏳ Loading customer details...</div>
            )}
            {selectedCustomer && !fetchingCustomer && (
              <div style={styles.selectedBadge}>
                ✓ {selectedCustomer.name}{selectedCustomer.companyName ? ` — ${selectedCustomer.companyName}` : ''}
                <button type="button" onClick={() => { setSelectedCustomer(null); setSearchTerm(''); }} style={{ marginLeft: 10, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>✕ Change</button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <label style={styles.label}>Guest Customer Name *</label>
            <input
              type="text"
              placeholder="Enter customer name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.input}
              required
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: 12 }}>
              <div>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  placeholder="guest@email.com"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  placeholder="+44 7700 000000"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div>
                <label style={styles.label}>Company Name</label>
                <input
                  type="text"
                  placeholder="Company Ltd"
                  value={form.companyName}
                  onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div>
                <label style={styles.label}>Company Number</label>
                <input
                  type="text"
                  placeholder="12345678"
                  value={form.companyNumber}
                  onChange={(e) => setForm(f => ({ ...f, companyNumber: e.target.value }))}
                  style={styles.input}
                />
              </div>
            </div>
          </div>
        )}

        {/* Credit Balance Banner */}
        {!isGuest && creditBalance > 0 && (
          <div style={styles.creditBanner}>
            <span>💰 <strong>£{creditBalance.toFixed(2)}</strong> credit available for this customer —</span>
            <button type="button" style={styles.creditApplyBtn} onClick={() => setShowCreditModal(true)}>
              Apply now?
            </button>
          </div>
        )}

        {/* Applied Credit notice */}
        {appliedCredit > 0 && (
          <div style={styles.creditApplied}>
            ✅ £{appliedCredit.toFixed(2)} credit will be applied to this invoice
            <button type="button" style={{ marginLeft: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={() => setAppliedCredit(0)}>Remove</button>
          </div>
        )}

        {/* Auto-filled fields */}
        {selectedCustomer && (
          <div style={styles.autoFillGrid}>
            <div>
              <label style={styles.label}>Email</label>
              <input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} style={styles.input} placeholder="Customer email" />
            </div>
            <div>
              <label style={styles.label}>Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} style={styles.input} placeholder="Customer phone" />
            </div>
            <div>
              <label style={styles.label}>Company Name</label>
              <input value={form.companyName} onChange={e => setForm(f => ({...f, companyName: e.target.value}))} style={styles.input} placeholder="Company name" />
            </div>
            <div>
              <label style={styles.label}>Company Number</label>
              <input value={form.companyNumber} onChange={e => setForm(f => ({...f, companyNumber: e.target.value}))} style={styles.input} placeholder="Company number" />
            </div>
          </div>
        )}

        <label style={styles.label}>Customer Address</label>
        <textarea
          value={form.customerAddress}
          onChange={(e) => setForm({ ...form, customerAddress: e.target.value })}
          style={{ ...styles.input, height: '60px', resize: 'vertical' as const }}
        />
      </div>

      {/* Invoice Details */}
      <div style={styles.row}>
        <div style={styles.col}>
          <label style={styles.label}>Invoice Number *</label>
          <input
            value={form.invoiceNumber}
            onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
            style={styles.input}
            disabled={isLocked}
            required
          />
        </div>
        <div style={styles.col}>
          <label style={styles.label}>Invoice Date *</label>
          <input
            type="date"
            value={form.invoiceDate}
            onChange={(e) => {
              const newDate = e.target.value;
              const days = PAYMENT_TERMS[paymentTerms] ?? 30;
              setForm(f => ({ ...f, invoiceDate: newDate, dueDate: calcDueDate(newDate, days) }));
            }}
            style={styles.input}
            disabled={isLocked}
            required
          />
        </div>
        <div style={styles.col}>
          <label style={styles.label}>Payment Terms</label>
          <select
            value={paymentTerms}
            disabled={isLocked}
            onChange={e => {
              const term = e.target.value;
              setPaymentTerms(term);
              const days = PAYMENT_TERMS[term] ?? 30;
              setForm(f => ({ ...f, dueDate: calcDueDate(f.invoiceDate, days) }));
            }}
            style={{ ...styles.input, marginBottom: 6 }}
          >
            {Object.keys(PAYMENT_TERMS).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={styles.col}>
          <label style={styles.label}>Due Date *</label>
          <input
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            style={styles.input}
            disabled={isLocked}
            required
          />
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: -10, marginBottom: 10 }}>
            {!isGuest && selectedCustomer
              ? `Customer: Net ${creditTermsDays} — ${paymentTerms} applied`
              : `${paymentTerms} applied`}
          </div>
        </div>
      </div>

      <label style={styles.label}>Description *</label>
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        style={{ ...styles.input, height: '80px', resize: 'vertical' as const }}
        placeholder="Description of goods or services..."
        disabled={isLocked}
        required
      />

      {/* Items Table */}
      <h3 style={styles.itemsTitle}>Invoice Items</h3>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Product</th>
              <th style={styles.th}>Description</th>
              <th style={styles.thCenter}>Qty</th>
              <th style={styles.thRight}>Unit Price (£)</th>
              <th style={styles.thCenter}>Disc %</th>
              <th style={styles.thCenter}>VAT %</th>
              <th style={styles.thRight}>Line Total</th>
              <th style={styles.thCenter}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const gross = (item.quantity || 0) * (item.unitPrice || 0);
              const discountAmt = gross * ((item.discount || 0) / 100);
              const lineTotal = gross - discountAmt;
              return (
                <tr key={idx} style={styles.tableRow}>
                  <td style={styles.td}>
                    <select
                      value={item.productId || ''}
                      onChange={(e) => handleProductSelect(idx, e.target.value)}
                      style={styles.select}
                    >
                      <option value="">— Select product —</option>
                      {products.map(p => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name} — £{p.price.toFixed(2)}
                        </option>
                      ))}
                    </select>
                    {item.productId && item.box_quantity && item.box_quantity > 0 && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <button type="button" onClick={() => handleSaleTypeChange(idx, 'unit')}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1.5px solid #3b82f6', background: item.saleType === 'unit' ? '#3b82f6' : 'transparent', color: item.saleType === 'unit' ? '#fff' : '#3b82f6', cursor: 'pointer', fontWeight: 600 }}>
                          Unit
                        </button>
                        <button type="button" onClick={() => handleSaleTypeChange(idx, 'box')}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1.5px solid #8b5cf6', background: item.saleType === 'box' ? '#8b5cf6' : 'transparent', color: item.saleType === 'box' ? '#fff' : '#8b5cf6', cursor: 'pointer', fontWeight: 600 }}>
                          📦 Box ({item.box_quantity} units)
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                      style={styles.inputSmall}
                      placeholder="Or type..."
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      value={item.quantity}
                      min="1"
                      onChange={(e) => handleItemChange(idx, 'quantity', Number(e.target.value))}
                      style={{ ...styles.inputSmall, width: '60px', textAlign: 'center' }}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      value={item.unitPrice}
                      min="0"
                      step="0.01"
                      onChange={(e) => handleItemChange(idx, 'unitPrice', Number(e.target.value))}
                      style={{ ...styles.inputSmall, width: '90px', textAlign: 'right' }}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      value={item.discount}
                      min="0"
                      max="100"
                      step="0.5"
                      onChange={(e) => handleItemChange(idx, 'discount', Number(e.target.value))}
                      style={{ ...styles.inputSmall, width: '60px', textAlign: 'center' }}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      value={item.vatRate}
                      min="0"
                      step="0.01"
                      onChange={(e) => handleItemChange(idx, 'vatRate', Number(e.target.value))}
                      style={{ ...styles.inputSmall, width: '60px', textAlign: 'center' }}
                    />
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    {item.discount > 0 && (
                      <div style={{ fontSize: 11, color: '#ef4444', textDecoration: 'line-through' }}>£{gross.toFixed(2)}</div>
                    )}
                    <strong style={{ color: '#10b981' }}>£{lineTotal.toFixed(2)}</strong>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        style={styles.removeBtn}
                      >
                        ✖
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!isLocked && (
        <button type="button" onClick={addItem} style={styles.addBtn}>
          ➕ Add Item
        </button>
      )}

      {/* Tax Mode Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '10px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', width: 'fit-content' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>VAT Mode:</span>
        {(['exclusive', 'inclusive'] as const).map(mode => (
          <button key={mode} type="button"
            disabled={isLocked}
            onClick={() => setTaxMode(mode)}
            style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: isLocked ? 'default' : 'pointer',
              fontWeight: 600, fontSize: 13,
              background: taxMode === mode ? '#3b82f6' : '#e5e7eb',
              color: taxMode === mode ? '#fff' : '#6b7280',
            }}>
            {mode === 'exclusive' ? 'VAT Exclusive (add on top)' : 'VAT Inclusive (already in price)'}
          </button>
        ))}
      </div>

      {/* Totals */}
      <div style={styles.totals}>
        <div style={styles.totalRow}>
          <span>Subtotal:</span>
          <span>£{totals.subtotal.toFixed(2)}</span>
        </div>
        {totals.discountTotal > 0 && (
          <div style={{ ...styles.totalRow, color: '#ef4444' }}>
            <span>Discount:</span>
            <span>-£{totals.discountTotal.toFixed(2)}</span>
          </div>
        )}
        <div style={styles.totalRow}>
          <span>VAT:</span>
          <span>£{totals.vatAmount.toFixed(2)}</span>
        </div>
        {appliedCredit > 0 && (
          <div style={{ ...styles.totalRow, color: '#059669' }}>
            <span>Credit Applied:</span>
            <span>-£{appliedCredit.toFixed(2)}</span>
          </div>
        )}
        <div style={styles.totalRowFinal}>
          <span>Amount Due:</span>
          <span>£{totals.total.toFixed(2)}</span>
        </div>
      </div>

      {/* Status Lifecycle Display */}
      <div style={{ marginBottom: 20, padding: '16px 20px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Invoice Status Lifecycle</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(['Draft', 'Sent', 'Paid', 'Overdue', 'Cancelled'] as const).map(s => {
            const isCurrent = invoiceStatus === s;
            return (
              <button key={s} type="button"
                onClick={() => setInvoiceStatus(s)}
                style={{
                  padding: '8px 16px', borderRadius: 20, border: `2px solid ${STATUS_COLORS[s]}`,
                  background: isCurrent ? STATUS_COLORS[s] : 'transparent',
                  color: isCurrent ? '#fff' : STATUS_COLORS[s],
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}>{s}</button>
            );
          })}
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
          Select the status this invoice will be saved with. After generating, status can be updated from the invoice list.
        </div>
      </div>

      {/* Payment Terms reminder auto-note */}
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', fontSize: 13, color: '#065f46' }}>
        <strong>Payment reminder:</strong> {paymentTerms === 'Due on Receipt' ? 'Payment is due immediately upon receipt.' : `Payment due within ${PAYMENT_TERMS[paymentTerms]} days (${paymentTerms}). `}
        Due date: <strong>{form.dueDate}</strong>.
      </div>

      {/* Notes */}
      <label style={styles.label}>Notes (Optional)</label>
      <textarea
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        style={{ ...styles.input, height: '90px', resize: 'vertical' as const }}
        placeholder={`Payment instructions or additional notes...\n\nDefault: Payment due ${paymentTerms === 'Due on Receipt' ? 'immediately' : `within ${PAYMENT_TERMS[paymentTerms]} days`}. Bank details: ...`}
        disabled={isLocked}
      />

      {/* Submit buttons */}
      {/* Submit buttons — always visible on create form */}
      <div className="invoice-submit-row" style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <ActionButton
          variant="save"
          label={submitting ? 'Saving...' : 'Save as Draft'}
          onClick={async () => {
            if (submitting) return;
            setInvoiceStatus('Draft');
            await new Promise(r => setTimeout(r, 50));
            document.getElementById('invoiceSubmitBtn')?.click();
          }}
          style={{ flex: 1, width: '100%' }}
        />
        <button type="button"
          onClick={() => setShowPreview(true)}
          style={{ flex: 1, padding: '12px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15, boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}>
          👁 Preview Invoice
        </button>
        <ActionButton
          variant="add"
          label={submitting ? 'Creating...' : 'Generate Invoice'}
          onClick={() => { if (!submitting) document.getElementById('invoiceSubmitBtn')?.click(); }}
          style={{ flex: 1, width: '100%' }}
        />
        <button id="invoiceSubmitBtn" type="submit" style={{ display: 'none' }} disabled={submitting} />
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, minWidth: 560, maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '3px solid #10b981', paddingBottom: 12 }}>
              <div>
                <h2 style={{ margin: 0, color: '#1f2937' }}>🧾 Invoice Preview</h2>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{form.invoiceNumber}</div>
              </div>
              <button type="button" onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Bill To:</strong>
              <div>{selectedCustomer?.name || searchTerm}</div>
              {form.email && <div style={{ color: '#6b7280', fontSize: 13 }}>{form.email}</div>}
              {form.phone && <div style={{ color: '#6b7280', fontSize: 13 }}>{form.phone}</div>}
              {form.companyName && <div style={{ color: '#6b7280', fontSize: 13 }}>{form.companyName}</div>}
              {form.customerAddress && <div style={{ color: '#6b7280', fontSize: 13, whiteSpace: 'pre-line' }}>{form.customerAddress}</div>}
            </div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 13 }}>
              <div><strong>Invoice Date:</strong> {form.invoiceDate}</div>
              <div><strong>Due Date:</strong> {form.dueDate}</div>
              <div><strong>Status:</strong> {form.status}</div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
              <thead>
                <tr style={{ background: '#f3f4f6' }}>
                  <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Description</th>
                  <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>Qty</th>
                  <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #e5e7eb' }}>Unit Price</th>
                  <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>Disc %</th>
                  <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>VAT %</th>
                  <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #e5e7eb' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.filter(i => i.description && i.quantity > 0).map((item, idx) => {
                  const gross = item.quantity * item.unitPrice;
                  const net = gross * (1 - (item.discount || 0) / 100);
                  return (
                    <tr key={idx}>
                      <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{item.description}</td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>{item.quantity}</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #e5e7eb' }}>£{item.unitPrice.toFixed(2)}</td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>{item.discount || 0}%</td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>{item.vatRate}%</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #e5e7eb' }}>£{net.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ maxWidth: 260, marginLeft: 'auto', fontSize: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span>Subtotal:</span><span>£{totals.subtotal.toFixed(2)}</span></div>
              {totals.discountTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#ef4444' }}><span>Discount:</span><span>-£{totals.discountTotal.toFixed(2)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span>VAT:</span><span>£{totals.vatAmount.toFixed(2)}</span></div>
              {appliedCredit > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#059669' }}><span>Credit Applied:</span><span>-£{appliedCredit.toFixed(2)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16, paddingTop: 8, borderTop: '2px solid #10b981' }}><span>Amount Due:</span><span>£{totals.total.toFixed(2)}</span></div>
            </div>
            {/* Payment reminder in preview */}
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, fontSize: 13, color: '#065f46', border: '1px solid #bbf7d0' }}>
              <strong>Payment Terms:</strong> {paymentTerms} — Due <strong>{form.dueDate}</strong>
              {taxMode === 'inclusive' && <span style={{ marginLeft: 12, color: '#6b7280' }}>(VAT inclusive)</span>}
            </div>
            {form.notes && <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#6b7280' }}><strong>Notes:</strong> {form.notes}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" style={{ ...styles.submitBtn, background: '#6b7280', flex: 1 }} onClick={() => setShowPreview(false)}>✏️ Back to Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Credit Modal */}
      {showCreditModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ marginTop: 0 }}>💰 Apply Credit</h3>
            <p>Available credit: <strong>£{creditBalance.toFixed(2)}</strong></p>
            <label style={styles.label}>Amount to apply (£)</label>
            <input
              type="number"
              min={0}
              max={creditBalance}
              step={0.01}
              defaultValue={Math.min(creditBalance, totals.total)}
              id="creditApplyInput"
              style={styles.input}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button type="button" style={{ ...styles.submitBtn, background: '#10b981', flex: 1 }}
                onClick={() => {
                  const val = parseFloat((document.getElementById('creditApplyInput') as HTMLInputElement).value) || 0;
                  setAppliedCredit(Math.min(val, creditBalance, totals.total));
                  setShowCreditModal(false);
                }}
              >Apply</button>
              <button type="button" style={{ ...styles.submitBtn, background: '#6b7280', flex: 1 }} onClick={() => setShowCreditModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </form>
    </>
  );
};

const styles: any = {
  form: {
    maxWidth: '900px',
    margin: '30px auto',
    padding: '40px',
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    paddingBottom: '20px',
    borderBottom: '3px solid #10b981'
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937'
  },
  invoiceNumber: {
    padding: '8px 16px',
    background: '#3b82f6',
    color: '#fff',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '14px'
  },
  section: {
    marginBottom: '25px',
    padding: '20px',
    background: '#f9fafb',
    borderRadius: '12px',
    border: '1px solid #e5e7eb'
  },
  sectionTitle: {
    margin: '0 0 15px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151'
  },
  toggleButtons: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px'
  },
  toggleBtn: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    flex: 1,
    transition: 'all 0.2s',
    fontSize: '14px'
  },
  toggleBtnActive: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(59,130,246,0.3)'
  },
  toggleBtnInactive: {
    backgroundColor: '#fff',
    color: '#6b7280',
    border: '2px solid #e5e7eb'
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    fontWeight: '600',
    marginBottom: '6px',
    fontSize: '14px',
    color: '#374151'
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    marginBottom: '15px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
    zIndex: 1000,
    maxHeight: '280px',
    overflowY: 'auto',
    marginTop: '-10px'
  },
  dropdownItem: {
    padding: '12px 15px',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer'
  },
  dropdownAddress: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#6b7280'
  },
  selectedBadge: {
    padding: '12px',
    background: '#d1fae5',
    borderRadius: '8px',
    border: '2px solid #10b981',
    marginTop: '-10px',
    marginBottom: '15px',
    color: '#059669',
    fontWeight: '600'
  },
  row: {
    display: 'flex',
    gap: '15px',
    marginBottom: '0'
  },
  col: {
    flex: 1
  },
  itemsTitle: {
    marginTop: '30px',
    marginBottom: '15px',
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937'
  },
  tableWrapper: {
    overflowX: 'auto',
    marginBottom: '20px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb'
  },
  table: {
    width: '100%',
    minWidth: '700px',
    borderCollapse: 'collapse',
    background: '#fff'
  },
  tableHeader: {
    background: '#f3f4f6'
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '700',
    color: '#6b7280',
    textTransform: 'uppercase',
    borderBottom: '2px solid #e5e7eb'
  },
  thCenter: {
    padding: '12px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#6b7280',
    textTransform: 'uppercase',
    borderBottom: '2px solid #e5e7eb'
  },
  thRight: {
    padding: '12px',
    textAlign: 'right',
    fontSize: '12px',
    fontWeight: '700',
    color: '#6b7280',
    textTransform: 'uppercase',
    borderBottom: '2px solid #e5e7eb'
  },
  tableRow: {
    borderBottom: '1px solid #f3f4f6'
  },
  td: {
    padding: '12px'
  },
  select: {
    width: '100%',
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px'
  },
  inputSmall: {
    width: '100%',
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px'
  },
  removeBtn: {
    color: '#ef4444',
    background: 'none',
    border: 'none',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  addBtn: {
    marginBottom: '25px',
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    background: '#10b981',
    color: '#fff',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
    transition: 'all 0.2s'
  },
  totals: {
    marginTop: '25px',
    padding: '20px',
    borderTop: '2px solid #10b981',
    borderRadius: '12px',
    background: '#f0fdf4',
    maxWidth: '350px',
    marginLeft: 'auto',
    marginBottom: '25px'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    fontSize: '15px',
    color: '#6b7280'
  },
  totalRowFinal: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937'
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '16px',
    marginTop: '10px',
    boxShadow: '0 4px 16px rgba(59,130,246,0.4)',
    transition: 'all 0.2s'
  },
  creditBanner: {
    background: '#fffbeb',
    border: '1px solid #fbbf24',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    color: '#92400e'
  },
  creditApplyBtn: {
    background: '#f59e0b',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13
  },
  creditApplied: {
    background: '#d1fae5',
    border: '1px solid #10b981',
    borderRadius: 8,
    padding: '10px 16px',
    marginBottom: 16,
    fontSize: 14,
    color: '#065f46'
  },
  autoFillGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0 16px',
    marginBottom: 4
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    padding: 30,
    minWidth: 340,
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
  }
};

export default InvoiceForm;
